// Quote Builder — pricing math.
//
// Mirrors kos-qb-v3.js:740-855. All aggregation is done in integer pence to
// avoid float drift. Item fields remain GBP for display/round-trip compat
// with the legacy localStorage payload; we convert on every sum.

import type { QuoteBuilderState, QuoteItem, QuoteTotals } from "./types";
import { pencify } from "./formatters";

export function calcTrade(listGbp: number, discPct: number): number {
  return Math.max(0, (listGbp || 0) * (1 - (discPct || 0) / 100));
}

/**
 * Trade price of a line, including any components that the line's discount
 * must NOT touch.
 *
 * For every supplier except SJB this is just calcTrade(list, disc) — the
 * addend is undefined and reads as 0, so behaviour is unchanged. SJB cabinet
 * lines carry `sjbHardwareTotal`: hinges and drawer boxes are charged at full
 * list because the board-group adjustment applies to the carcass only.
 * Folding hardware into `listPrice` instead would silently discount it.
 */
function lineTrade(item: QuoteItem): number {
  return +(calcTrade(item.listPrice || 0, item.discPct || 0) + (item.sjbHardwareTotal || 0)).toFixed(4);
}

export function calcRetailPct(tradeGbp: number, markupPct: number): number {
  return Math.max(0, (tradeGbp || 0) * (1 + (markupPct || 0) / 100));
}

export function calcRetailGbp(tradeGbp: number, markupGbp: number): number {
  return Math.max(0, (tradeGbp || 0) + (markupGbp || 0));
}

/**
 * Recalculate an item's pricing chain after a field change.
 *
 * `changedField` controls which direction the chain flows:
 *   listPrice/discPct → tradePrice → retailPrice
 *   tradePrice → discPct (back-calc), retailPrice (forward)
 *   markupPct/markupGbp → retailPrice
 *   retailPrice → markupPct + markupGbp (back-calc)
 */
export function recalcItem(
  item: QuoteItem,
  changedField: keyof QuoteItem | null,
): QuoteItem {
  const lp = item.listPrice || 0;
  const mPct = item.markupPct || 0;
  const mGbp = item.markupGbp || 0;
  let tp = item.tradePrice || 0;

  switch (changedField) {
    case "listPrice":
    case "discPct":
      item.tradePrice = lineTrade(item);
      tp = item.tradePrice;
      item.retailPrice = item.markupMode === "gbp"
        ? +calcRetailGbp(tp, mGbp).toFixed(4)
        : +calcRetailPct(tp, mPct).toFixed(4);
      break;
    case "tradePrice": {
      // Back-calc the discount from the CARCASS portion only — the hardware
      // is at list and was never discounted, so including it would report a
      // smaller board discount than the one actually applied.
      const carcassTp = tp - (item.sjbHardwareTotal || 0);
      if (lp > 0) item.discPct = +(((1 - carcassTp / lp) * 100).toFixed(2));
      item.retailPrice = item.markupMode === "gbp"
        ? +calcRetailGbp(tp, mGbp).toFixed(4)
        : +calcRetailPct(tp, mPct).toFixed(4);
      break;
    }
    case "markupPct":
      item.markupMode = "pct";
      item.retailPrice = +calcRetailPct(tp, mPct).toFixed(4);
      if (tp > 0) item.markupGbp = +(tp * mPct / 100).toFixed(2);
      break;
    case "markupGbp":
      item.markupMode = "gbp";
      item.retailPrice = +calcRetailGbp(tp, mGbp).toFixed(4);
      if (tp > 0) item.markupPct = +((mGbp / tp) * 100).toFixed(2);
      break;
    case "retailPrice": {
      const rp = item.retailPrice || 0;
      if (tp > 0) {
        item.markupPct = +(((rp / tp) - 1) * 100).toFixed(2);
        item.markupGbp = +(rp - tp).toFixed(2);
      }
      break;
    }
    default:
      // Generic recompute (e.g. after construction).
      item.tradePrice = lineTrade(item);
      tp = item.tradePrice;
      item.retailPrice = item.markupMode === "gbp"
        ? +calcRetailGbp(tp, mGbp).toFixed(4)
        : +calcRetailPct(tp, mPct).toFixed(4);
      break;
  }
  return item;
}

/**
 * Aggregate totals over all zones. Everything stays in integer pence so
 * floats can't accumulate error across a 100-line kitchen quote.
 */
export function quoteTotals(state: QuoteBuilderState): QuoteTotals {
  let listCostPence = 0;
  let tradeCostPence = 0;
  let retailSubPence = 0;
  let lineCount = 0;
  // `items` = number of lines (used by the internal PDF "Lines" column);
  // `qty` = total quantity of products across those lines (used by the
  // customer-irrelevant "Items" breakdown shown to the retailer).
  const bySupplier: Record<string, { tradePence: number; retailPence: number; items: number; qty: number }> = {};

  for (const z of state.zones) {
    for (const it of z.items) {
      const qty = it.qty || 0;
      // SJB lines split the carcass (listPrice) from hardware charged at full
      // list, so the list-cost column has to pick up both or it understates.
      const lpP = pencify((it.listPrice || 0) + (it.sjbHardwareTotal || 0)) * qty;
      const tpP = pencify(it.tradePrice || 0) * qty;
      const rpP = pencify(it.retailPrice || 0) * qty;
      listCostPence += lpP;
      tradeCostPence += tpP;
      retailSubPence += rpP;
      lineCount++;
      const sup = it.supplier || "Manual";
      const slot = bySupplier[sup] ?? { tradePence: 0, retailPence: 0, items: 0, qty: 0 };
      slot.tradePence += tpP;
      slot.retailPence += rpP;
      slot.items += 1;
      slot.qty += qty;
      bySupplier[sup] = slot;
    }
  }

  // Source treats delivery as a separate customer-facing charge with its
  // own VAT line (kos-qb-v3.js:8612-8617). It is NOT part of retailSub —
  // keeping it separate preserves the modular ex-VAT margin formula
  // (project_qb_design_decisions G34: margin = profit / retailSub_ex_VAT
  // — delivery would inflate the denominator artificially).
  const deliveryPence = state.deliveryChargePence || 0;
  const deliveryVatPence = Math.round(deliveryPence * (state.vatRate / 100));

  const discountPence = listCostPence - tradeCostPence;
  const profitPence = retailSubPence - tradeCostPence;
  const marginPct = retailSubPence > 0 ? (profitPence / retailSubPence) * 100 : 0;
  const vatAmountPence = Math.round(retailSubPence * (state.vatRate / 100));
  const grandTotalPence = retailSubPence + vatAmountPence + deliveryPence + deliveryVatPence;

  return {
    listCostPence,
    tradeCostPence,
    discountPence,
    retailSubPence,
    profitPence,
    marginPct,
    deliveryPence,
    deliveryVatPence,
    vatAmountPence,
    grandTotalPence,
    lineCount,
    bySupplier,
  };
}

/**
 * Distribute a target £ profit across all line items, proportionally to
 * each item's share of total trade cost. Switches every line into £-markup
 * mode. Returns `true` if any change was made.
 */
export function distributeProfitGbp(state: QuoteBuilderState, totalProfitGbp: number): boolean {
  const t = quoteTotals(state);
  if (t.tradeCostPence <= 0 || totalProfitGbp < 0) return false;
  const targetProfitPence = pencify(totalProfitGbp);
  for (const z of state.zones) {
    for (const it of z.items) {
      const qty = it.qty || 0;
      if (qty <= 0) continue;
      // Snapshot the pre-target margin on the FIRST apply so Reset can restore
      // it (a second apply before a reset keeps the original baseline).
      if (it.preTargetMarkupMode === undefined) {
        it.preTargetMarkupMode = it.markupMode;
        it.preTargetMarkupPct  = it.markupPct;
        it.preTargetMarkupGbp  = it.markupGbp;
      }
      const itTradePence = pencify(it.tradePrice || 0) * qty;
      const share = t.tradeCostPence > 0 ? itTradePence / t.tradeCostPence : 0;
      const unitMarkupPence = Math.round((targetProfitPence * share) / qty);
      it.markupMode = "gbp";
      it.markupGbp = +(unitMarkupPence / 100).toFixed(2);
      it.retailPrice = +calcRetailGbp(it.tradePrice || 0, it.markupGbp).toFixed(4);
      it.markupPct = (it.tradePrice || 0) > 0
        ? +(((it.retailPrice / it.tradePrice) - 1) * 100).toFixed(2)
        : 0;
    }
  }
  return true;
}

/**
 * Reset every line item's margin back to the snapshot taken before a "Set Total
 * Profit" target was applied. Clears the snapshot. Returns `true` if any line
 * was reset (i.e. a target had been applied).
 */
export function resetProfitTarget(state: QuoteBuilderState): boolean {
  let changed = false;
  for (const z of state.zones) {
    for (const it of z.items) {
      if (it.preTargetMarkupMode === undefined) continue;
      it.markupMode = it.preTargetMarkupMode;
      it.markupPct  = it.preTargetMarkupPct ?? 0;
      it.markupGbp  = it.preTargetMarkupGbp ?? 0;
      const tp = it.tradePrice || 0;
      it.retailPrice = it.markupMode === "gbp"
        ? +calcRetailGbp(tp, it.markupGbp).toFixed(4)
        : +calcRetailPct(tp, it.markupPct).toFixed(4);
      delete it.preTargetMarkupMode;
      delete it.preTargetMarkupPct;
      delete it.preTargetMarkupGbp;
      changed = true;
    }
  }
  return changed;
}

/**
 * Distribute by target margin %. Equation:
 *   margin = profit / retailSub
 *   ⇒ profit = trade * (m / (1 - m))
 */
export function distributeMarginPct(state: QuoteBuilderState, marginPct: number): boolean {
  const m = marginPct / 100;
  if (m <= 0 || m >= 0.99) return false;
  const t = quoteTotals(state);
  if (t.tradeCostPence <= 0) return false;
  const totalProfitPence = Math.round(t.tradeCostPence * (m / (1 - m)));
  return distributeProfitGbp(state, totalProfitPence / 100);
}

/**
 * Distribute by target markup % — profit as a percentage of TRADE COST.
 *
 *   markup = profit / trade   ⇒   profit = trade * mk
 *
 * This is the cost-plus basis every per-row "Markup %" on the quote already
 * uses (calcRetailPct), and it is NOT the basis distributeMarginPct uses. The
 * two live side by side in the Totals panel: "Margin 28.6%" next to rows
 * marked "Markup 40%", describing the same job. A retailer typing 35 into the
 * job-level % box was getting a 35% MARGIN — a 53.8% markup — with nothing on
 * screen naming the basis. Hence the toggle, and hence this function: so the
 * markup side is a real calculation rather than a relabelled margin.
 */
export function distributeMarkupPct(state: QuoteBuilderState, markupPct: number): boolean {
  const mk = markupPct / 100;
  if (mk <= 0) return false;
  const t = quoteTotals(state);
  if (t.tradeCostPence <= 0) return false;
  return distributeProfitGbp(state, (t.tradeCostPence * mk) / 100);
}

/** markup = margin / (1 − margin). Both as percentages. */
export function markupPctFromMargin(marginPct: number): number {
  const m = marginPct / 100;
  if (m <= 0) return 0;
  if (m >= 1) return Infinity;
  return (m / (1 - m)) * 100;
}

/** margin = markup / (1 + markup). Both as percentages. */
export function marginPctFromMarkup(markupPct: number): number {
  const mk = markupPct / 100;
  if (mk <= 0) return 0;
  return (mk / (1 + mk)) * 100;
}
